import { ContactQuarantinedMessageBatchFilter, FilterDirection, QuarantineStatus, useGetQuarantinedMessagesLazyQuery } from '@graphql/generated';
import { SpamQueueRow } from '../types';
import { useCallback, useEffect, useRef, useState } from 'react';

type SpamQueueData = {
  data: {
    rows: SpamQueueRow[];
    canGetMore: boolean;
  };
};

export const useGetSpamQueue = (cursor: SpamQueueRow['cursor'] | null, filter: ContactQuarantinedMessageBatchFilter): SpamQueueData => {
  // const data = mock_data;
  const lastCursor = useRef(cursor);
  const lastFilter = useRef(filter);
  const hasInit = useRef(false);
  const [canGetMore, setCanGetMore] = useState(true);
  const state = useRef(0);

  const [allRows, setAllRows] = useState<SpamQueueRow[]>([]);
  const [getRows, { data, loading, called }] = useGetQuarantinedMessagesLazyQuery({
    batchMode: 'off',
    fetchPolicy: 'no-cache',
  });

  const canMakeRequest = state.current === 0 || state.current === 2;

  // Sends a request
  const makeRequest = useCallback(() => {
    state.current = 0;
    const sortDirection = filter.quarantineStatus === QuarantineStatus.review ? FilterDirection.asc : FilterDirection.desc;

    getRows({
      variables: {
        filter,
        sort: {
          createdAt: sortDirection,
        },
        first: 50,
        after: cursor,
      },
    });
  }, [cursor, filter, getRows]);

  // Runs only on start, fetches initial data
  useEffect(() => {
    if (!hasInit.current && canMakeRequest) {
      makeRequest();
      hasInit.current = true;
    }
  });

  // Runs when query state changes
  // Moves from init to loading to done.
  useEffect(() => {
    if (canMakeRequest && called && loading) {
      // Initial state of query
      state.current = 1;
    } else if (state.current == 1 && called && !loading) {
      // Query completed loading
      state.current = 2;
      const newRows =
        data?.contactQuarantinedMessageBatchContainer.pagedResults.edges.map(edge => {
          return {
            ...edge.node,
            cursor: edge.cursor,
          };
        }) ?? [];

      if (newRows.length === 0) {
        setCanGetMore(false);
      } else {
        setCanGetMore(true);
      }

      setAllRows(oldRows => {
        return [...oldRows, ...newRows];
      });
    }
  }, [called, canMakeRequest, data, loading]);

  // Runs when cursor has changed (User presses load more)
  // And query is either in init state or done state.
  // Fetches more from after new cursor
  useEffect(() => {
    if (lastCursor.current !== cursor && canMakeRequest) {
      lastCursor.current = cursor;
      makeRequest();
    } else if (lastFilter.current !== filter && canMakeRequest) {
      lastFilter.current = filter;
      setAllRows([]);
      makeRequest();
    }
  }, [cursor, canMakeRequest, makeRequest, filter]);

  return {
    data: { rows: allRows, canGetMore },
  };
};
